import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, useAuth } from '@mondivago/supabase';
import { MessageRow } from '@mondivago/types';
import type { ChatParticipant } from '@mondivago/types';

export type { ChatParticipant };

/**
 * Hook to manage chat messages and participants.
 */
export function useChat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [participants, setParticipants] = useState<Record<string, ChatParticipant | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = (await supabase
        .from('comm_messages')
        .select('*')
        .or(`sender_id.eq.${user.id.toString()},recipient_id.eq.${user.id.toString()}`)
        .order('created_at', { ascending: true })
        .limit(600)) as { data: MessageRow[] | null; error: unknown };

      if (dbError) {
        throw new Error('Database error');
      }
      const nextMessages = data ?? [];
      setMessages(nextMessages);

      const otherUserIds = Array.from(
        new Set(
          nextMessages
            .map((msg) => {
              const otherId = msg.sender_id === user.id ? msg.recipient_id : msg.sender_id;
              return otherId;
            })
            .filter((id): id is string => !!id && id !== user.id)
        )
      );

      if (otherUserIds.length > 0) {
        const [suppliersRes, travelersRes] = await Promise.all([
          supabase.from('suppliers').select('user_id, company_name, photo_url').in('user_id', otherUserIds),
          supabase.from('travelers').select('user_id, first_name, last_name, photo_url').in('user_id', otherUserIds),
        ]);

        const lookup: Record<string, ChatParticipant> = {};

        (suppliersRes.data ?? []).forEach((g) => {
          lookup[g.user_id] = {
            user_id: g.user_id,
            first_name: g.company_name ?? 'Supplier',
            last_name: '',
            photo_url: g.photo_url,
          };
        });

        (travelersRes.data ?? []).forEach((t) => {
          if (!lookup[t.user_id]) {
            lookup[t.user_id] = {
              user_id: t.user_id,
              first_name: t.first_name,
              last_name: t.last_name,
              photo_url: t.photo_url,
            };
          }
        });

        setParticipants(lookup);
      }
    } catch (err) {
      setError('Error loading chat. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const resolveParticipant = useCallback(
    async (userId: string) => {
      const existing = participants[userId];
      if (existing) return existing;

      const [supplierRes, travelerRes] = await Promise.all([
        supabase.from('suppliers').select('user_id, company_name, photo_url').eq('user_id', userId).maybeSingle(),
        supabase
          .from('travelers')
          .select('user_id, first_name, last_name, photo_url')
          .eq('user_id', userId)
          .maybeSingle(),
      ]);

      const candidate = supplierRes.data ?? travelerRes.data;
      if (candidate) {
        const participant = {
          user_id: candidate.user_id,
          first_name: 'company_name' in candidate ? (candidate.company_name ?? 'Supplier') : candidate.first_name,
          last_name: 'company_name' in candidate ? '' : candidate.last_name,
          photo_url: candidate.photo_url,
        };
        setParticipants((prev) => ({ ...prev, [userId]: participant }));
        return participant;
      }

      // No suppliers/travelers profile row — fall back to the auth.users display
      // name so the participant resolves for any registered user.
      const { data: identity } = await supabase.rpc('get_user_display_name', { p_user_id: userId });
      const row = (Array.isArray(identity) ? identity[0] : identity) as { first_name: string; last_name: string } | null;
      if (row && (row.first_name || row.last_name)) {
        const participant: ChatParticipant = {
          user_id: userId,
          first_name: row.first_name || 'User',
          last_name: row.last_name ?? '',
          photo_url: null,
        };
        setParticipants((prev) => ({ ...prev, [userId]: participant }));
        return participant;
      }

      return undefined;
    },
    [participants]
  );

  const getParticipantDisplayName = useCallback(
    async (userId: string) => {
      const participant = await resolveParticipant(userId);
      if (!participant) return 'Someone';
      return `${participant.first_name} ${participant.last_name ?? ''}`.trim();
    },
    [resolveParticipant]
  );

  const sendMessage = useCallback(
    async (recipientId: string, body: string, bookingId?: string) => {
      if (!user) return { success: false, error: 'Not authenticated' };

      try {
        const insertPayload = {
          sender_id: user.id,
          recipient_id: recipientId,
          body: body.trim(),
          ...(bookingId ? { booking_id: bookingId } : {}),
        };

        const { data, error: dbError } = await supabase
          .from('comm_messages')
          .insert(insertPayload)
          .select('*')
          .single();

        if (dbError) throw dbError;
        if (data) {
          setMessages((prev) => [...prev, data]);
        }
        return { success: true, data };
      } catch (err) {
        return { success: false, error: 'Error sending message. Please try again.' };
      }
    },
    [user]
  );

  const markAsRead = useCallback(
    async (messageIds: string[]) => {
      if (!user || messageIds.length === 0) return;

      try {
        const nowIso = new Date().toISOString();
        const { error: dbError } = await supabase
          .from('comm_messages')
          .update({ read_at: nowIso })
          .in('id', messageIds)
          .eq('recipient_id', user.id);

        if (dbError) throw dbError;
        setMessages((prev) => prev.map((msg) => (messageIds.includes(msg.id) ? { ...msg, read_at: nowIso } : msg)));
        return { success: true };
      } catch (err) {
        return { success: false, error: 'Error updating read status. Please try again.' };
      }
    },
    [user]
  );

  const deleteConversation = useCallback(
    async (otherUserId: string) => {
      if (!user) return { success: false, error: 'Not authenticated' };

      try {
        const { error: dbError } = await supabase
          .from('comm_messages')
          .delete()
          .or(
            `and(sender_id.eq.${user.id.toString()},recipient_id.eq.${otherUserId.toString()}),and(sender_id.eq.${otherUserId.toString()},recipient_id.eq.${user.id.toString()})`
          );

        if (dbError) throw dbError;

        setMessages((prev) =>
          prev.filter(
            (msg) =>
              !(msg.sender_id === user.id && msg.recipient_id === otherUserId) &&
              !(msg.sender_id === otherUserId && msg.recipient_id === user.id)
          )
        );

        return { success: true };
      } catch (err) {
        return { success: false, error: 'Error deleting conversation. Please try again.' };
      }
    },
    [user]
  );

  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!user) return;

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current).catch(() => {});
    }

    const channelName = `public:comm_messages:${user.id}-${Math.random().toString(36).substring(2, 9)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'comm_messages', filter: `recipient_id=eq.${user.id}` },
        (payload) => {
          const newMessage = payload.new as MessageRow;
          setMessages((prev) => (prev.some((m) => m.id === newMessage.id) ? prev : [...prev, newMessage]));
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'comm_messages', filter: `sender_id=eq.${user.id}` },
        (payload) => {
          const newMessage = payload.new as MessageRow;
          setMessages((prev) => (prev.some((m) => m.id === newMessage.id) ? prev : [...prev, newMessage]));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'comm_messages', filter: `recipient_id=eq.${user.id}` },
        (payload) => {
          const updatedMessage = payload.new as MessageRow;
          setMessages((prev) => prev.map((m) => (m.id === updatedMessage.id ? updatedMessage : m)));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'comm_messages', filter: `sender_id=eq.${user.id}` },
        (payload) => {
          const updatedMessage = payload.new as MessageRow;
          setMessages((prev) => prev.map((m) => (m.id === updatedMessage.id ? updatedMessage : m)));
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel).catch(() => {});
    };
  }, [user]);

  useEffect(() => {
    void fetchMessages();
  }, [fetchMessages]);

  const activeConversations = Object.keys(
    messages.reduce<Record<string, boolean>>((acc, msg) => {
      const otherId = msg.sender_id === user?.id ? msg.recipient_id : msg.sender_id;
      if (otherId) acc[otherId] = true;
      return acc;
    }, {})
  ).length;

  // Count unread messages for the current user (messages received but not read)
  const unreadCount = messages.filter((msg) => msg.recipient_id === user?.id && !msg.read_at).length;

  return {
    messages,
    participants,
    loading,
    error,
    activeConversations,
    unreadCount,
    refresh: fetchMessages,
    sendMessage,
    markAsRead,
    deleteConversation,
    resolveParticipant,
    getParticipantDisplayName,
  };
}
